import * as React from 'react';
import { useCallback, useState, memo } from 'react';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { ImportSheet } from './process';
import { 
    Typography,
    CircularProgress,
    Button,
    Card,
    Box
} from '@mui/material';
import HttpClient from '../../providers/HttpClient';
import ApiResources from '../../providers/ApiResources';
import { queryString } from '../../common/UtilFunctions';
import { useDefineAppLocation } from 'src/enterprise/ra-navigation';
import { useNotify } from "react-admin";
import ListIcon from '@mui/icons-material/List';
import { PromoExcelModal } from './process/commands';

export const ImportProcess = () => {
    useDefineAppLocation('admin/importer');
    const notify = useNotify();
    const [sheetNames, setSheetNames] = useState<any>([]);
    const [sheets, setSheets] = useState<any>([]);
    const [sheetMappers, setSheetMappers] = useState<any>([]);
    const [importedFileName, setImportedFileName] = useState<string>('');
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [loggerData, setLoggerData] = useState<any>([]);
    const [promoExcelModal, setPromoExcelModal] = useState(false);

    const onDrop = useCallback((acceptedFiles) => {
        acceptedFiles.forEach((file) => {
            setIsFileLoading(true);
            setImportedFileName(file.name);

            const reader = new FileReader()

            reader.onabort = () => console.log('File reading was aborted')
            reader.onerror = () => console.log('File reading has failed')
            reader.onload = () => {
                let excelDocument = XLSX.read(reader.result, {
                    type: 'array',
                    cellDates: true,
                    dateNF: 'MM/DD/YYYY'
                });

                if (excelDocument.SheetNames.length > 0) {
                    setSheetNames(excelDocument.SheetNames);

                    let sheetData = {};
                    excelDocument.SheetNames.map((sheetName: string) => {
                        const jsonData = XLSX.utils.sheet_to_json(excelDocument.Sheets[sheetName], {
                            defval: "",
                            raw: false
                        });
                        sheetData[sheetName] = jsonData;
                    });

                    setSheets(sheetData);
                    loadSheetMappers();
                }
            }
            reader.readAsArrayBuffer(file)
        })
    }, [])
    const {getRootProps, getInputProps} = useDropzone({
        onDrop, 
        multiple: false,
        accept: {
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
        }
    })

    const loadSheetMappers = () => {
        const query = {
            page: 1,
            limit: 100,
        };

        HttpClient(ApiResources.getImporterSheets(queryString(query)), {
            method: 'GET'
        }).then(({ json }) => {
            setSheetMappers(json.results);
            setIsFileLoading(false);

            return;
        }).catch(error => {
            notify(error.message, { type: 'error' });
        });
    }

    const logger = (data) => {
        setLoggerData((current)=>{
            return [...current, data];
        });
    }

    const handleClearLogger = () => {
        setLoggerData([]);
    }

    const setPromoExcelModalHandler = (value: boolean) => {
        setPromoExcelModal(value);
    }

    const handleOnInsert = (file) => {
        onDrop(file);
    }

    return (
        <Root>
            <Box marginY="10px" display="flex" gap="5px" alignSelf="end" justifyContent="end">
                <Button onClick={() => setPromoExcelModalHandler(true)} variant="outlined" size="small">
                    <ListIcon sx={{ fontSize: "18px"}} />
                    <Typography component="span" sx={{lineHeight: "1.75", fontSize: "14px", marginLeft: "8px", fontWeight: 600}}>Generate Promo Excel</Typography>
                </Button>
            </Box>

            <Card className={classes.uploadContainer}>
                <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <Typography variant="body1" sx={{ fontSize: '16px', padding: '15px', cursor: 'pointer', textAlign: 'center' }}>
                    { importedFileName !== '' ?
                        <span>{importedFileName}</span>
                    :
                        <span>Drag and drop excel file here, or click to select</span>
                    }
                    </Typography>
                </div>
            </Card>
            { isFileLoading ?
                <div className={classes.sheetContainer} style={{ textAlign: 'center', paddingTop: '50px' }}>
                    <CircularProgress size={60}/>
                </div>
            :
                <div className={classes.sheetContainer}>
                    {sheetNames.map((sheetName: string, sheetIndex: number) => {
                        let coulmns = Object.keys(sheets?.[sheetName]?.[0]); 
                        let sheetMapper = sheetMappers.find(s => s.name === sheetName);

                        return (
                            <ImportSheet 
                                key={sheetIndex} 
                                name={sheetName} 
                                columns={coulmns} 
                                sheet={sheets?.[sheetName]} 
                                sheetMapper={sheetMapper}
                                logger={logger}
                            />
                        );
                    })}
                </div>
            }

            <Card className={classes.logContainer} sx={{ maxWidth: '60%' }}>
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Button 
                        variant="contained" 
                        sx={{ textTransform: 'none', margin: '15px' }} 
                        size="small"
                        onClick={handleClearLogger}
                    >
                        Clear Log
                    </Button>
                </div>
                <Card className={classes.logContainerContent}>
                    {loggerData.map((item: any, index: number) => {
                        return (
                            <MemoizedLogItem key={index} item={item} index={index}/>
                        );
                    })}
                </Card>
            </Card> 

            {promoExcelModal &&
                <PromoExcelModal
                    open={promoExcelModal}
                    setOpen={setPromoExcelModalHandler}
                    onInsert={handleOnInsert}
                />
            }
        </Root>
    )
}

function LogItem({ item, index }) {
  return (
    <div className="importerLogBlock">
        <Typography variant="body2" sx={{ display: 'inline-block' }}>
            <strong>Product:</strong> {item?.primary || '-'},
        </Typography>
        <Typography variant="body2" sx={{ textTransform: 'capitalize', display: 'inline-block', marginLeft: '5px' }} className={'importerLogStatus-'+item?.status}>
            <strong>{item?.status}</strong>
        </Typography>

        { item?.error &&
        <Typography variant="body2" color="red">
            {item.error}
        </Typography>
        }


        { item?.success &&
        <div style={{ marginTop: '5px' }}>
            <Typography variant="body2" sx={{ display: 'inline-block', marginRight: '5px' }} color="green">
                <strong>Successful:</strong>
            </Typography>
            {Object.keys(item.success).map((code: string) => {
                let value = item.success[code];

                return (
                    <Typography key={code} variant="body2" sx={{ display: 'inline-block', marginRight: '5px' }}>
                        <strong>{code}:</strong> {value.join(', ')},
                    </Typography>
                );
            })}
        </div>
        }

        { item?.fail &&
        <div style={{ marginTop: '5px' }}>
            <Typography variant="body2" sx={{ display: 'inline-block', marginRight: '5px' }} color="red">
                <strong>Failed:</strong>
            </Typography>
            {Object.keys(item.fail).map((code: string) => {
                let value = item.fail[code];

                return (
                    <Typography key={code} variant="body2" sx={{ display: 'inline-block', marginRight: '5px' }}>
                        <strong>{code}:</strong> {value.join(', ')}
                    </Typography>
                );
            })}
        </div>
        }

        { item?.assign &&
        <div style={{ marginTop: '5px' }}>
            <Typography variant="body2" sx={{ display: 'inline-block', marginRight: '5px' }} color="orange">
                <strong>Assigned:</strong>
            </Typography>
            {Object.keys(item.assign).map((code: string) => {
                let value = item.assign[code];

                return (
                    <Typography key={code} variant="body2" sx={{ display: 'inline-block', marginRight: '5px' }}>
                        <strong>{code}:</strong> {value.join(', ')}
                    </Typography>
                );
            })}
        </div>
        }
    </div>
  );
}

export const MemoizedLogItem = memo(LogItem);

const PREFIX = 'ImportProcess';
const classes = {
    uploadContainer: `${PREFIX}-uploadContainer`,
    sheetContainer: `${PREFIX}-sheetContainer`,
    logContainer: `${PREFIX}-logContainer`,
    logContainerContent: `${PREFIX}-logContainer-content`,
};
const Root = styled('div', {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
    display: 'inline-block',
    marginTop: '20px',
    [`& .`+classes.sheetContainer]: {
        width: '50%',
        display: 'inline-block',
    },
    [`& .`+classes.logContainer]: {
        width: '50%',
        float: 'right',
        marginTop: '30px',
        display: 'inline-block',
    },
    [`& .`+classes.logContainerContent]: {
        borderTop: '1px solid #e9e9ea',
        borderRadius: '0px',
        maxHeight: '600px',
        overflowY: 'scroll',
        overscrollBehaviorY: 'contain',
        scrollSnapType: 'y proximity'
    },
    [`& .importerLogStatus-updated`]: {
        color: 'green'
    },
    [`& .importerLogStatus-skipped`]: {
        color: 'red'
    },
    [`& .importerLogStatus-failed`]: {
        color: 'red'
    },
    [`& .importerLogStatus-created`]: {
        color: 'orange'
    },
    [`& .importerLogBlock`]: {
        '&:last-child': { 
            borderBottom: 'none',
            scrollSnapAlign: 'end'
        },
        borderBottom: '1px solid #e9e9ea', 
        margin: '15px', 
        paddingBottom: '15px', 
        marginBottom: '0px'
    }
}));